import { loadModules } from 'esri-loader';
import React from 'react';
import ReactDOM from 'react-dom';
import BSLAExternalLinkComponent from '../../helpers/BSLAExternalLinkComponent';
import { DateTime } from 'luxon';
import BSLAPopupItemComponent from '../../helpers/BSLAPopupItemComponent';
import BSLASortLayers from '../../helpers/BSLASortLayers';
import { STATEWIDE_SCALE } from '../../BSLAConstants';

const BSLABaldEagleLayer = async () => {
    type layerModules = [typeof import('esri/layers/FeatureLayer'), typeof import('esri/layers/GroupLayer')];
    const [FeatureLayer, GroupLayer] = await (loadModules(['esri/layers/FeatureLayer', 'esri/layers/GroupLayer']) as Promise<layerModules>);

    const popupContent = {
        outFields: ['*'],
        content: (feature: __esri.Feature) => {
            const atts = feature.graphic.attributes;
            const node = document.createElement('div');

            const content = (
                <React.Fragment>
                    <BSLAPopupItemComponent label="Nest ID" value={atts.NestID} />
                    <BSLAPopupItemComponent label="Last Known Active Date" value={atts.LastKnownA} />
                    <BSLAPopupItemComponent label="Last Survey Date" value={atts.LastSurvey} />
                    {atts.County} County
                    <div className="mt-3 mb-1">
                        <p>
                            Issue: This layer displays the regulated 660' buffer around known bald eagle nests according to the FWC. Standard guidelines prohibit development within 100’
                            of a nest at any time. Development in the area 100-330’ from a nest cannot occur during the nesting season (mid-September until early April).
                            Development in the area 330-660’ from a nest is permitted in the non-nesting period, but can only occur during the nesting season if the nest is
                            monitored by a qualified biologist. Some nests identified in this layer may have other non-standard protection zones. Generally an intact nest requires
                            five consecutive years of abandonment before it is de-listed while a downed nest requires two years. Because FWC does not review all nests each year,
                            some nests may lack data, others may be overdue for de-listing, and other new nests may not yet be recorded in the database. Contact a qualified
                            environmental consultant to review the site and to determine the exact status of nests on or nearby the search parcel.
                        </p>
                        <p>Cost: Costs range from $5,000 in consulting fees to complete loss of development potential.</p>
                    </div>
                    <BSLAExternalLinkComponent href="http://myfwc.com/wildlifehabitats/managed/bald-eagle" />
                </React.Fragment>
            );

            ReactDOM.render(content, node);

            return node;
        }
    };

    const fwcBufferLayer = new FeatureLayer({
        title: "Bald Eagle Nests 660' Buffers (FWC)",
        url: 'http://utility.arcgis.com/usrsvcs/servers/c2781df3064640e08d4ee67a2aeee460/rest/services/BALD_EAGLE_NEST_V2/FeatureServer/1',
        minScale: 100000,
        visible: false,
        popupTemplate: {
            title: "Bald Eagle Nests 660' Buffers (FWC)",
            ...popupContent
        }
    });

    const fwcNestLayer = new FeatureLayer({
        title: 'Bald Eagle Nests (FWC)',
        url: 'http://utility.arcgis.com/usrsvcs/servers/c2781df3064640e08d4ee67a2aeee460/rest/services/BALD_EAGLE_NEST_V2/FeatureServer/0',
        visible: false,
        minScale: STATEWIDE_SCALE,
        popupTemplate: {
            title: 'Bald Eagle Nests (FWC)',
            ...popupContent
        }
    });

    const capeCoralNests = new FeatureLayer({
        title: 'Bald Eagle Nests (Cape Coral)',
        url: 'https://capeims.capecoral.net/arcgis/rest/services/OpenData/DCD/MapServer/8',
        visible: false,
        minScale: STATEWIDE_SCALE,
        definitionExpression: "TYPE LIKE '%Eagle%'",
        popupTemplate: {
            outFields: ['*'],
            title: 'Bald Eagle Nests (Cape Coral)',
            content: (feature: __esri.Feature) => {
                const atts = feature.graphic.attributes;
                const node = document.createElement('div');

                const content = (
                    <React.Fragment>
                        <BSLAPopupItemComponent label="Eagle ID" value={atts.EAGLE_ID} />
                        <BSLAPopupItemComponent label="Status" value={atts.STATUS} />
                        <BSLAPopupItemComponent label="Local Name" value={atts.LOCAL_NAME ?? 'N/A'} />
                        <BSLAPopupItemComponent label="Date Inspected" value={atts.DATEINSP ? DateTime.fromMillis(atts.DATEINSP).toFormat('MMM dd, yyyy') : 'N/A'} />
                        <BSLAPopupItemComponent label="Comments" value={atts.COMMENTS ? atts.COMMENTS : 'N/A'} />
                        <div className="mt-3">
                            <BSLAExternalLinkComponent href="https://www.capecoral.net/department/community_development/environmental/bald_eagle_information.php" />
                        </div>
                    </React.Fragment>
                );

                ReactDOM.render(content, node);

                return node;
            }
        }
    });

    return new GroupLayer({
        layers: [fwcBufferLayer, fwcNestLayer, capeCoralNests].sort(BSLASortLayers),
        visible: true,
        title: 'Bald Eagles'
    });
};

export default BSLABaldEagleLayer;
